import {
  AddCampagneDTO,
  CampagneModel,
  UpdateCampagneDTO,
} from "@/app/entities";
import { useAuthStore, useCampagneStore } from "@/context";
import useApi from "@/services/use.api";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useToast } from "../toast";
import { ObjectToQueryParams } from "@/utils";
import { useAuth } from "../auth";
import { UserTypes, UserTypesCodes } from "../users";

const { usePostApi, useGetApi, useDeleteApi } = useApi();

export const useCampagne = () => {
  const [loading, setLoading] = useState(false);
  const {
    filterForm,
    paginatedItems,
    setCampagnes,
    setPaginatedItems,
    setFilterForm,
  } = useCampagneStore();
  const { currentUser } = useAuthStore();
  const navigate = useNavigate();

  const campagnes = paginatedItems.content as CampagneModel[];

  const getCampagneFetchType = () => {
    return [UserTypes.PARTICULIER, UserTypes.ENTREPRISE].includes(
      currentUser?.type as UserTypes
    )
      ? UserTypesCodes.Eleveur
      : UserTypesCodes.Technique;
  };

  const fetchCampagnes = async (
    params?: { type?: string; page?: number },
    reset?: boolean
  ) => {
    // if (campagnes.length && !reset) return;
    setLoading(true);
    const fetchType = getCampagneFetchType();
    const param = ObjectToQueryParams({
      page: params?.page || 0,
      typeCampagne: params?.type || filterForm.type,
      dateCreation: filterForm.creationDate,
      draft:
        filterForm.draft !== ""
          ? filterForm.draft === "OUI"
            ? true
            : false
          : undefined,
      shelve:
        filterForm.shelve !== ""
          ? filterForm.shelve === "OUI"
            ? true
            : false
          : undefined,
    });
    const res = await useGetApi<{
      content: CampagneModel[];
      empty: boolean;
      first: boolean;
      last: boolean;
      number: number;
      numberOfElements: number;
      pageable: any;
      size: number;
      sort: any;
      totalElements: number;
      totalPages: number;
    }>(`/auth/${fetchType}/campagne${param}`);
    const { data } = res ?? {};

    setPaginatedItems({
      content: data?.content || [],
      empty: data?.empty || true,
      first: data?.first || true,
      last: data?.last || true,
      number: data?.number || 0,
      numberOfElements: data?.numberOfElements || 0,
      pageable: data?.pageable || {},
      size: data?.size || 0,
      sort: data?.sort || {},
      totalElements: data?.totalElements || 0,
      totalPages: data?.totalPages || 0,
    });
    setLoading(false);
  };

  const saveCampagne = async (params: AddCampagneDTO) => {
    setLoading(true);
    const res = await usePostApi(`/auth/admin/campagne`, params);
    const { success, message, data } = res ?? {};

    setLoading(false);
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return;
    }

    setCampagnes([data as CampagneModel, ...campagnes]);
    setPaginatedItems({
      ...paginatedItems,
      content: [data as CampagneModel, ...campagnes],
    });
    useToast("Nouvelle campagne ajoutée avec succès.", {
      variant: "success",
    });
    navigate("/dashboard/campagnes");
  };

  const updateCampagne = async (params: UpdateCampagneDTO) => {
    setLoading(true);
    const res = await usePostApi<CampagneModel | { error: string }>(
      `/auth/admin/campagne`,
      params
    );
    const { success, message, data } = res ?? {};

    setLoading(false);
    if (!success) {
      const errorData = data as { error: string };
      useToast(errorData?.error || message || "Une erreur s'est produite", {
        variant: "error",
      });
      return;
    }
    const campagne = data as CampagneModel;
    const index = campagnes.findIndex(
      (c) => c.reference === campagne.reference
    );
    if (index !== -1) {
      const newCampagnes = [...campagnes];
      newCampagnes[index] = campagne;
      setCampagnes(newCampagnes);
      setPaginatedItems({
        ...paginatedItems,
        content: newCampagnes,
      });
    }
    useToast("Campagne modifiée avec succès.", {
      variant: "success",
    });
    navigate("/dashboard/campagnes");
  };

  const suspendCampagne = async (reference: string) => {
    setLoading(true);
    const res = await useDeleteApi<CampagneModel>(
      `/auth/admin/campagne/${reference}/${true}`
    );
    const { success, message, data } = res ?? {};
    setLoading(false);

    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return;
    }

    const index = campagnes.findIndex((c) => c.reference === reference);
    if (data && index !== -1) {
      const newCampagnes = [...campagnes];
      newCampagnes[index] = data;
      setCampagnes(newCampagnes);
      useToast("Campagne suspendue avec succès.", {
        variant: "success",
      });
    }
  };

  const getCampagneByReference = (
    reference: string
  ): CampagneModel | undefined => {
    return campagnes.find(
      (campagne: CampagneModel) => campagne.reference === reference
    );
  };

  const getCampagneStatusLabel = (campagne?: CampagneModel) => {
    const statusLabel = campagne?.draft
      ? "Brouillon"
      : campagne?.shelve
      ? "Suspendu"
      : "Démarrée";

    return statusLabel;
  };

  const getCampagneStatusCode = (campagne?: CampagneModel) => {
    const statusCode = campagne?.draft
      ? "pending"
      : campagne?.shelve
      ? "inactive"
      : "active";

    return statusCode;
  };

  const doDeclaration = async (reference: string, params: any) => {
    setLoading(true);
    const res = await usePostApi(
      `/auth/eleveur/enrollement/${reference}`,
      params
    );
    const { success, message, data } = res ?? {};
    setLoading(false);

    // if (!success) {
    //   useToast(data ?? message ?? "Une erreur s'est produite", {
    //     variant: "error",
    //   });
    //   return;
    // }

    // const index = campagnes.findIndex((c) => c.reference === reference);
    useToast("Déclaration effectuée avec succès.", {
      variant: "success",
    });
    navigate("/dashboard/campagnes");
  };

  return {
    loading,
    campagnes,
    filterForm,
    paginatedItems,
    updateCampagne,
    setFilterForm,
    fetchCampagnes,
    saveCampagne,
    suspendCampagne,
    getCampagneByReference,
    getCampagneStatusLabel,
    getCampagneStatusCode,
    doDeclaration,
  };
};
